import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import {
  Button,
  Edit as EditIcon,
  Trash as TrashIcon,
  Tooltip
  // @ts-ignore
} from '@netbasequid/core'
import { TimelineAnnotation } from 'schemas/TimelineAnnotationSchema'
import { getYearDateHourMinuteDisplayNumeric } from 'utils/timeZoneUtil'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { toExternal } from 'vo/util'
import AnnotationIcon from './AnnotationIcon'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'

const AnnotationContentTitleButtons = styled.div`
  display: none;
`

export const AnnotationCardWrapper = styled.div<{ $theme: SystemTheme }>`
  display: flex;
  gap: 8px;
  padding: 8px 0;
  margin: 0px 16px;

  & + div {
    border-top: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  }

  &:last-child {
    border-bottom: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  }

  &:hover ${AnnotationContentTitleButtons} {
    display: flex;
  }
`

const AnnotationContent = styled.div<{ $theme: SystemTheme }>`
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
  color: ${({ $theme }) => $theme.text.primary};

  & > div:nth-child(2) {
    word-break: break-word;
  }

  & > div:last-child {
    margin-top: 2px;
    color: ${({ $theme }) => $theme.text.placeholder};
  }
`

const AnnotationContentTitle = styled.div<{ $theme: SystemTheme }>`
  font-weight: ${({ $theme }) => $theme.fontWeight.medium};
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  & > div:first-child {
    flex: 1;
    line-height: 24px;
  }
`

const StyledButton = styled(Button)<{ $theme: SystemTheme }>`
  border-radius: 50%;
  width: 24px;
  height: 24px;
  padding: 5px;
`

const AnnotationCard = ({
  annotation,
  onEdit,
  onDelete,
  isDeleting,
  operable
}: {
  annotation?: TimelineAnnotation
  onEdit?: (annotationId: number) => void
  onDelete?: (annotationId: TimelineAnnotation) => void
  isDeleting?: boolean
  operable?: boolean
}) => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const userConfig = usePageConfigUser()
  const [confirmDialog, setConfirmDialog] = useState(false)

  if (!annotation) return null
  return (
    <AnnotationCardWrapper $theme={theme}>
      <div>
        <AnnotationIcon color={annotation.color!} style={{ marginTop: '6px' }} />
      </div>
      <AnnotationContent $theme={theme}>
        <AnnotationContentTitle $theme={theme}>
          <div>{annotation?.owner}</div>
          {operable && (
            <AnnotationContentTitleButtons>
              <Tooltip mode={mode} content={t('edit')} followCursor={false}>
                <StyledButton mode={mode} variant="ghost" onClick={() => onEdit?.(annotation.id!)}>
                  <EditIcon fontSize={16} />
                </StyledButton>
              </Tooltip>
              <Tooltip mode={mode} content={t('delete')} followCursor={false}>
                <StyledButton
                  mode={mode}
                  variant="ghost"
                  onClick={() => {
                    setConfirmDialog(true)
                  }}>
                  <TrashIcon fontSize={16} />
                </StyledButton>
              </Tooltip>
            </AnnotationContentTitleButtons>
          )}
        </AnnotationContentTitle>
        <div>{annotation?.description}</div>
        <div>
          {`(${getYearDateHourMinuteDisplayNumeric(
            annotation.startDatetime!,
            userConfig.timeZone
          )} - ${getYearDateHourMinuteDisplayNumeric(
            annotation.endDatetime! - 1,
            userConfig.timeZone
          )})`}
        </div>
      </AnnotationContent>
      <ConfirmDeleteDialog
        open={confirmDialog}
        isDeleting={isDeleting}
        onClose={() => setConfirmDialog(false)}
        onOk={() => {
          onDelete?.(toExternal(annotation))
        }}
      />
    </AnnotationCardWrapper>
  )
}

export default AnnotationCard
